import { Link, useLocation } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { BugIcon, FolderKanbanIcon, LogOutIcon, MenuIcon, SettingsIcon, UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

import { useLogout } from "@/shared/hooks/useLogout";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyDetails } from "@/shared/lib/services/user";
import { User } from "@/shared/lib/types/user";
import { cn } from "@/shared/lib/utils/cn";
import { AppError } from "@/shared/lib/utils/errors";

interface Props {
  permissions: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileNav({ permissions, open, onOpenChange }: Props) {
  const location = useLocation();
  const { logout, isLoggingOut } = useLogout();

  const {
    data: user,
    isPending: isUserPending,
    error: userError,
  } = useQuery<User, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_DETAILS],
    queryFn: getMyDetails,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="md:hidden" aria-label="Open navigation menu">
          <MenuIcon className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BugIcon className="size-4 text-primary" />
            BugTracker
          </SheetTitle>
        </SheetHeader>
        <div className="flex items-center gap-3 px-8 pb-4">
          {isUserPending || userError ? (
            <Skeleton className="size-10 rounded-full shrink-0" />
          ) : (
            <Avatar size="lg">
              <AvatarImage src="" alt={[user.firstName, user.lastName].join(" ")} />
              <AvatarFallback>{[user.firstName[0].toUpperCase(), user.lastName[0].toUpperCase()].join("")}</AvatarFallback>
            </Avatar>
          )}
          <div className="min-w-0 flex flex-col gap-1.5">
            {isUserPending || userError ? (
              <>
                <Skeleton className="h-3.5 w-28 rounded" />
                <Skeleton className="h-3 w-36 rounded" />
              </>
            ) : (
              <>
                <p className="truncate text-sm font-semibold">{[user.firstName, user.lastName].join(" ")}</p>
                <p className="truncate text-xs text-muted-foreground">{user.emailAddress}</p>
              </>
            )}
          </div>
        </div>
        <Separator />
        {permissions.includes(PERMISSIONS.USER_PROJECTS_READ) && (
          <>
            <nav className="flex flex-col gap-1 px-4 py-4">
              <SheetClose asChild>
                <Link
                  to="/projects"
                  className={cn(
                    "flex items-center gap-3 rounded-none px-3 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors hover:bg-accent hover:text-accent-foreground",
                    location.pathname.startsWith("/projects") ? "bg-accent text-accent-foreground" : "text-foreground"
                  )}
                >
                  <FolderKanbanIcon className="size-4 shrink-0" />
                  Projects
                </Link>
              </SheetClose>
            </nav>
            <Separator />
          </>
        )}
        <nav className="flex flex-col gap-1 px-4 py-4">
          <SheetClose asChild>
            <Link to="/profile" className="flex items-center gap-3 rounded-none px-3 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors hover:bg-accent hover:text-accent-foreground">
              <UserIcon className="size-4 shrink-0" />
              Profile
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link to="/notifications" className="flex items-center gap-3 rounded-none px-3 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors hover:bg-accent hover:text-accent-foreground">
              <BugIcon className="size-4 shrink-0" />
              Notifications
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link to="/settings" className="flex items-center gap-3 rounded-none px-3 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors hover:bg-accent hover:text-accent-foreground">
              <SettingsIcon className="size-4 shrink-0" />
              Settings
            </Link>
          </SheetClose>
          <button type="button" onClick={logout} disabled={isLoggingOut} className="w-full flex items-center gap-3 rounded-none px-3 py-2.5 text-xs font-semibold tracking-wider uppercase text-destructive transition-colors hover:bg-destructive/10">
            <LogOutIcon className="size-4 shrink-0" />
            {isLoggingOut ? "Signing out..." : "Sign out"}
          </button>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
